import axios from 'axios';
import jwtDecode from 'jwt-decode';
import ls, { clear, remove } from 'local-storage';

import axiosInterceptor from '../../utils/axios';
import {
  BodyIProps,
  ConfigIProps,
  IAuthState,
  IAuthToken,
  IBeginAuthRequest,
  IVerifyAuthRequest,
} from './authTypes';

// A  function making an async request for user token
export function getToken(data: IAuthToken, config: ConfigIProps) {
  return new Promise<any>((resolve, reject) => {
    const body: BodyIProps = {
      GrantType: 'authorization_code',
      Code: data.code,
      ClientId: config.clientId,
      RedirectUri: `https://${window.location.host}/login/callback`,
      CodeVerifier: data.codeChallenge,
    };

    const axiosConfig: any = {
      method: 'post',
      url: config.tokenURL,
      withCredentials: true,
      headers: {
        'Content-Type': 'application/json',
      },
      data: body,
    };

    axiosInterceptor(axiosConfig)
      .then((response) => {
        const userData: any = jwtDecode(response.data.IdToken);
        userData.initials =
          userData.given_name?.charAt(0) + userData.family_name?.charAt(0);
        ls('IdToken', response.data.IdToken);
        ls('RefreshToken', response.data.RefreshToken);
        ls('AccessToken', response.data.AccessToken);
        resolve(userData);
      })
      .catch((error) => {
        let message = error?.response?.data?.Message
          ? error.response.data.Message
          : error.message;
        if (error?.response?.status === 403) {
          message =
            'Oops! You do not have the permissions to access this page. If you believe this is incorrect, please contact your admin for resolution.';
        }
        reject(message);
      });
  });
}

// A  function making an async request for user token
export const getOffices = (
  userData: IAuthState['currentUser'],
  config: ConfigIProps
) => {
  return new Promise<any>((resolve, reject) => {
    const url = config.baseURL + '/order-management/region-details';
    const payload = {
      Email: userData.email,
    };
    const roleJson = JSON.parse(userData['user_roles'].toString());
    if (
      Object.keys(roleJson)?.includes('Notary_Users') &&
      Object.keys(roleJson)?.length === 1
    ) {
      ls('offices', {});
      remove('pkce_state');
      remove('pkce_code_verifier');
      resolve({});
    } else {
      axiosInterceptor
        .get(url, { params: payload })
        .then((response: any) => {
          ls('offices', response.data);
          remove('pkce_state');
          remove('pkce_code_verifier');
          resolve(response.data);
        })
        .catch((error) => {
          remove('IdToken');
          remove('RefreshToken');
          remove('AccessToken');
          reject(
            error?.response?.data?.Message
              ? 'Branch Request: ' + error.response.data.Message
              : 'Branch Request: ' + error.message
          );
        });
    }
  });
};

// A  function making an async request for user token
export function refreshToken(baseURL: string) {
  return new Promise<any>((resolve) => {
    const accessToken = ls('AccessToken');
    const idToken = ls('IdToken');
    const refreshToken = ls('RefreshToken');

    const axiosConfig: any = {
      method: 'post',
      url: baseURL + '/auth/refresh',
      withCredentials: true,
      headers: {
        Authorization: accessToken,
        Authentication: idToken,
        'X-Content-Type-Options': 'nosniff',
      },
      data: { RefreshToken: refreshToken },
    };

    axios(axiosConfig)
      .then((res) => {
        if (res?.data?.IdToken && res?.data?.AccessToken) {
          ls('IdToken', res.data.IdToken);
          ls('AccessToken', res.data.AccessToken);
          resolve(res.data);
        } else {
          resolve({ type: 'info', text: 'Your Session Expired' });
        }
        resolve({ type: 'info', text: 'Your Session Expired' });
      })
      .catch(() => {
        clear();
        resolve({ type: 'info', text: 'Your Session Expired' });
        window.location.replace('/');
      });
  });
}

export function startLogout(isDenied: boolean, config: ConfigIProps) {
  return new Promise<any>((resolve) => {
    const accessToken = ls('AccessToken');
    const idToken = ls('IdToken');
    const refreshToken = ls('RefreshToken');

    const axiosConfig: any = {
      method: 'post',
      url: config.baseURL + '/auth/revoke',
      withCredentials: true,
      headers: {
        Authorization: accessToken,
        Authentication: idToken,
        'X-Content-Type-Options': 'nosniff',
      },
      data: { RefreshToken: refreshToken },
    };

    axios(axiosConfig)
      .then(() => {
        clear();
        if (isDenied) {
          resolve({
            type: 'error',
            text: 'Oops! You do not have the permissions to access this page. If you believe this is incorrect, please contact your admin for resolution.',
          });
        } else {
          resolve({ type: 'success', text: 'Successfully logged out' });
        }
      })
      .catch(() => {
        clear();
        resolve({ type: 'error', text: 'Error logging out of session' });
      });
  });
}

export function beginAuth(data: IBeginAuthRequest, config: ConfigIProps) {
  return new Promise<any>((resolve, reject) => {
    const body = {
      UserId: data.userId,
      OrderId: data.orderId,
      IsCreateAccount: data.isCreateAccount,
    };
    const axiosConfig: any = {
      method: 'post',
      url: config.baseURL + '/auth/begin-authentication',
      headers: {
        'Content-Type': 'application/json',
      },
      data: body,
    };
    axiosInterceptor(axiosConfig)
      .then((response) => {
        resolve(response);
      })
      .catch(() => {
        clear();
        reject({
          type: 'error',
          text: 'There was an error with your authentication process',
        });
      });
  });
}

export function verifyAuth(data: IVerifyAuthRequest, config: ConfigIProps) {
  return new Promise<any>((resolve, reject) => {
    const body = {
      UserId: data.userId,
      OrderId: data.orderId,
      Session: data.session,
      IsCreateAccount: data.isCreateAccount,
      ...(data.answers && { Answers: data.answers }),
      ...(data.channel != undefined && { Channel: data.channel }),
      ...(data.resendMfaCode && { ResendMfaCode: data.resendMfaCode }),
      ...(data.reselectMfaChannel && {
        ReselectMfaChannel: data.reselectMfaChannel,
      }),
    };
    const axiosConfig: any = {
      method: 'post',
      url: config.baseURL + '/auth/verify-auth-challenge',
      headers: {
        'Content-Type': 'application/json',
      },
      data: body,
    };
    axiosInterceptor(axiosConfig)
      .then((response: any) => {
        if (
          response.data.id_token &&
          response.data.refresh_token &&
          response.data.access_token
        ) {
          const userData: any = jwtDecode(response.data.id_token);
          userData.initials =
            userData.given_name?.charAt(0) + userData.family_name?.charAt(0);
          userData.url = response.data.Url;
          ls('IdToken', response.data.id_token);
          ls('RefreshToken', response.data.refresh_token);
          ls('AccessToken', response.data.access_token);
          ls('IsGuest', true);
          resolve(userData);
        } else {
          resolve(response);
        }
      })
      .catch((e: any) => {
        if (
          e?.response?.data?.FaultedMembers &&
          e.response.data.FaultedMembers.includes('Session')
        ) {
          reject('Incorrect credentials, try again.');
        }
        reject('Error verifying your credentials');
        clear();
      });
  });
}
